import React from "react";
import useDegreeQueries from "../../../../degree/hooks/useDegreeQueries";
import { useSchoolId } from "../../../../school/hooks/useSchoolId";
import {
  IAcademicWizardConfigs,
  IWizardConfig,
  WizardMode,
  SubmitButtonColor,
} from "../../../../wizard/types";
import useAcademicQueries from "../../../hooks/useAcademicQueriesHigher";
import deactivateFormValidator from "../../validators/deactivateFormValidator";
import DeleteContent from "../../../../program/components/DeleteContent";
import { parseToFrontEndModel } from "../helpers/parseToFrontEndModel";
import { parseToBackEndModel } from "../helpers/parseToBackEndModel";
import { createContentConfig } from "../create/createContentConfig";
import { viewContentConfig } from "../view/viewContentConfig";
import { editContentConfig } from "../edit/editContentConfig";
import DeactivateContent from "../../../../../components/DeactivateContent/DeactivateContent";
import { getDeleteButtonConfigs } from "../../../../program/helpers/helperFunctions";
import { ENTITY_NAMES } from "../../../../../constants";

async function getCreateInitialData(fetchAllWithAcademic, schoolId) {
  const { data: degrees } = await fetchAllWithAcademic(schoolId);

  const formDegrees = parseToFrontEndModel(degrees);
  const selectedDegree =
    degrees.find((degree) => !degree.academic) || degrees[0];

  return {
    injectedData: {
      degrees,
    },
    selectedDegree,
    degrees: formDegrees,
  };
}

async function saveAcademic(data, createAcademic, schoolId) {
  if (data.selectedDegree.academic) {
    return null;
  }

  const parsedData = parseToBackEndModel(data, schoolId);

  return await createAcademic(parsedData);
}

function getButtonConfigs(values) {
  if (!!values.selectedDegree.academic) {
    // make view mode for existing degree
    return {
      submitButton: null,
      cancelButton: {
        text: "Close",
      },
    };
  }

  return {
    submitButton: {
      text: "Save",
      color: SubmitButtonColor.Success,
    },
    cancelButton: {
      text: "Close",
    },
  };
}

async function saveEditedAcademic(data, updateAcademic, schoolId, academicId) {
  const parsedData = parseToBackEndModel(data, schoolId, academicId);

  return await updateAcademic(parsedData);
}

async function getViewData(fetchOne, academicId) {
  const data = await fetchOne(academicId);

  return {
    injectedData: {
      data: data.data,
    },
  };
}

async function getAcademicDataForEdit(fetchOne, academicId) {
  const { data: data } = await fetchOne(academicId);

  const degree = {
    id: data.degreeId,
    name: data.degreeName,
    academic: {
      id: data.id,
      requiredDocs: data.requiredDocs,
      applicationForms: data.applicationForms,
      applicationLink: data?.applicationLink,
    },
  };

  const degrees = [degree];
  const formDegrees = parseToFrontEndModel(degrees);

  return {
    injectedData: {
      degrees,
    },
    selectedDegree: degree,
    degrees: formDegrees,
  };
}

async function getAppCount(fetch, id) {
  const { data } = await fetch({ academicId: id });

  return {
    injectedData: {
      approved: data.approved,
      pending: data.pending,
    },
  };
}

export function useAcademicConfig(): IAcademicWizardConfigs {
  const { fetchAllWithAcademic } = useDegreeQueries();
  const {
    fetchOne,
    activate,
    deactivate,
    deleteOne,
    create,
    update,
    fetchAppCount,
  } = useAcademicQueries();
  const schoolId = useSchoolId();

  const getCreateConfig = (): IWizardConfig => ({
    steps: [createContentConfig],
    submitButtonText: "Save",
    submitButtonColor: SubmitButtonColor.Success,
    mode: WizardMode.Create,
    getInitialData: () => getCreateInitialData(fetchAllWithAcademic, schoolId),
    saveCallback: (data) => saveAcademic(data, create, schoolId),
    getButtonConfigs: getButtonConfigs,
  });

  const getViewConfig = (id): IWizardConfig => ({
    steps: [viewContentConfig],
    submitButtonText: null,
    cancelButtonText: "Close",
    mode: WizardMode.View,
    getInitialData: () => getViewData(fetchOne, id),
  });

  const getEditConfig = (id): IWizardConfig => ({
    steps: [editContentConfig],
    submitButtonText: "Save",
    submitButtonColor: SubmitButtonColor.Success,
    mode: WizardMode.Edit,
    getInitialData: () => getAcademicDataForEdit(fetchOne, id),
    saveCallback: (data) => saveEditedAcademic(data, update, schoolId, id),
  });

  const getActivateConfig = (id): IWizardConfig => ({
    steps: [
      {
        titleConfig: {
          titleText: "Activate academic",
        },
        content: "Are you sure you want to activate this academic?",
      },
    ],
    submitButtonText: "Activate",
    cancelButtonText: "Close",
    saveCallback: () => activate(id),
  });

  const getDeactivateConfig = (id): IWizardConfig => ({
    steps: [
      {
        titleConfig: {
          titleText: "Deactivate academic",
        },
        width: "552px",
        content: () => <DeactivateContent name={ENTITY_NAMES.academic} />,
        validator: deactivateFormValidator,
      },
    ],
    submitButtonText: "Deactivate",
    submitButtonColor: SubmitButtonColor.Danger,
    cancelButtonText: "Close",
    getInitialData: () => getAppCount(fetchAppCount, id),
    saveCallback: (data) =>
      deactivate(id, {
        text: data?.reason ?? "",
      }),
  });

  const getDeleteConfig = (id): IWizardConfig => ({
    steps: [
      {
        width: "430px",
        titleConfig: {
          titleText: "Delete academic",
        },
        content: () => <DeleteContent name={ENTITY_NAMES.academic} />,
      },
    ],
    mode: WizardMode.Delete,
    getInitialData: () => getAppCount(fetchAppCount, id),
    saveCallback: () => deleteOne(id),
    getButtonConfigs: getDeleteButtonConfigs,
  });

  return {
    getCreateConfig,
    getViewConfig,
    getEditConfig,
    getActivateConfig,
    getDeactivateConfig,
    getDeleteConfig,
  };
}
